const DEFAULT_TRANSLATE_URL = 'https://libretranslate.de/translate';
const DEFAULT_MYMEMORY_TRANSLATE_URL = 'https://api.mymemory.translated.net/get';
const DEFAULT_MAX_TRANSLATE_LENGTH = 1200;
const DEFAULT_MYMEMORY_MAX_CHUNK = 400;

const defaultFetchJson = async (url, signal) => {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error('Wiki request failed.');
  }
  return response.json();
};

const extractSummaryFromHtml = (html, maxParagraphs = 2) => {
  if (!html) {
    return '';
  }
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const paragraphs = Array.from(doc.querySelectorAll('p'))
    .map((paragraph) => paragraph.textContent?.trim())
    .filter(Boolean);
  if (paragraphs.length === 0) {
    return (doc.body?.textContent || '').trim();
  }
  return paragraphs.slice(0, maxParagraphs).join('\n\n');
};

const findSectionMatch = (sections, matches) => {
  if (!Array.isArray(sections)) {
    return null;
  }
  return sections.find((section) => {
    const line = (section.line || '').toLowerCase();
    return matches.some((match) => line.includes(match));
  });
};

const splitTextIntoChunks = (text, maxLength) => {
  if (!text) {
    return [];
  }

  const chunks = [];
  const paragraphs = text.split(/\n{2,}/);

  const pushChunk = (chunk) => {
    const trimmed = chunk.trim();
    if (trimmed) {
      chunks.push(trimmed);
    }
  };

  const splitParagraph = (paragraph) => {
    const sentences = paragraph.split(/(?<=[.!?])\s+/);
    let current = '';

    sentences.forEach((sentence) => {
      const trimmed = sentence.trim();
      if (!trimmed) {
        return;
      }

      const next = current ? `${current} ${trimmed}` : trimmed;
      if (next.length > maxLength) {
        if (current) {
          pushChunk(current);
        }
        if (trimmed.length > maxLength) {
          let remaining = trimmed;
          while (remaining.length > maxLength) {
            pushChunk(remaining.slice(0, maxLength));
            remaining = remaining.slice(maxLength);
          }
          current = remaining.trim();
        } else {
          current = trimmed;
        }
      } else {
        current = next;
      }
    });

    if (current) {
      pushChunk(current);
    }
  };

  paragraphs.forEach((paragraph) => {
    const trimmed = paragraph.trim();
    if (!trimmed) {
      return;
    }
    if (trimmed.length <= maxLength) {
      pushChunk(trimmed);
    } else {
      splitParagraph(trimmed);
    }
  });

  return chunks;
};

const translateWithLibreTranslate = async (text, options) => {
  const { signal, translateUrl, translateApiKey } = options;
  const body = {
    q: text,
    source: 'auto',
    target: 'pt',
    format: 'text',
  };
  if (translateApiKey) {
    body.api_key = translateApiKey;
  }

  const response = await fetch(translateUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw new Error('Translate request failed.');
  }

  const payload = await response.json();
  return payload.translatedText || '';
};

const translateWithMyMemory = async (text, options) => {
  const { signal, myMemoryUrl, myMemoryMaxChunk } = options;
  const chunks = splitTextIntoChunks(text, myMemoryMaxChunk);
  if (chunks.length === 0) {
    return '';
  }

  const translatedChunks = [];
  for (const chunk of chunks) {
    const params = new URLSearchParams({
      q: chunk,
      langpair: 'en|pt',
    });
    const response = await fetch(`${myMemoryUrl}?${params.toString()}`, { signal });
    if (!response.ok) {
      throw new Error('Translate request failed.');
    }
    const payload = await response.json();
    const responseDetails = (payload.responseDetails || '').toLowerCase();
    const responseStatus = payload.responseStatus;
    const translatedText = payload.responseData?.translatedText || '';
    if (responseStatus && responseStatus !== 200) {
      throw new Error(payload.responseDetails || 'Translate request failed.');
    }
    if (
      !translatedText ||
      responseDetails.includes('query length limit') ||
      /query length limit/i.test(translatedText)
    ) {
      throw new Error('Translate request failed.');
    }
    translatedChunks.push(translatedText.trim());
  }

  return translatedChunks.join('\n\n');
};

const translateTextToPortuguese = async (text, options) => {
  if (!text) {
    return '';
  }
  const trimmed = text.trim();
  if (!trimmed) {
    return '';
  }

  const { maxTranslateLength } = options;

  try {
    const translated = await translateWithLibreTranslate(trimmed, options);
    if (
      translated &&
      translated.trim() &&
      translated.trim().toLowerCase() !== trimmed.toLowerCase()
    ) {
      return translated;
    }
  } catch {
    // Fallback below.
  }

  const shortened =
    trimmed.length > maxTranslateLength ? trimmed.slice(0, maxTranslateLength) : trimmed;
  const fallback = await translateWithMyMemory(shortened, options);
  if (fallback && fallback.trim()) {
    return fallback;
  }

  return '';
};

const buildWikiUrl = (wikiBaseUrl, title) => {
  if (!wikiBaseUrl || !title) {
    return '';
  }
  const slug = title.trim().replace(/\s+/g, '_');
  return `${wikiBaseUrl}/wiki/${encodeURIComponent(slug)}`;
};

const fetchWikiSections = (wikiApiUrl, title, signal, fetchJson) => {
  const params = new URLSearchParams({
    action: 'parse',
    format: 'json',
    origin: '*',
    page: title,
    prop: 'sections',
  });
  return fetchJson(`${wikiApiUrl}?${params.toString()}`, signal);
};

const fetchWikiSectionHtml = (wikiApiUrl, title, section, signal, fetchJson) => {
  const params = new URLSearchParams({
    action: 'parse',
    format: 'json',
    origin: '*',
    page: title,
    prop: 'text',
  });
  if (section !== undefined && section !== null) {
    params.set('section', String(section));
  }
  return fetchJson(`${wikiApiUrl}?${params.toString()}`, signal);
};

const fetchWikiPageByTitle = async (options) => {
  const { title, signal, wikiApiUrl, wikiBaseUrl, sectionConfig, fetchJson } = options;
  const sectionsPayload = await fetchWikiSections(wikiApiUrl, title, signal, fetchJson);
  if (!sectionsPayload || sectionsPayload.error || !sectionsPayload.parse) {
    return null;
  }
  const sections = sectionsPayload.parse?.sections ?? [];
  const pageTitle = sectionsPayload.parse?.title || title;
  const summarySections = [];

  for (const config of sectionConfig) {
    const match = findSectionMatch(sections, config.matches);
    if (!match) {
      continue;
    }
    const sectionPayload = await fetchWikiSectionHtml(
      wikiApiUrl,
      pageTitle,
      match.index,
      signal,
      fetchJson
    );
    if (!sectionPayload || sectionPayload.error || !sectionPayload.parse) {
      continue;
    }
    const sectionHtml = sectionPayload.parse?.text?.['*'] || '';
    const text = extractSummaryFromHtml(sectionHtml, config.maxParagraphs);
    if (text) {
      summarySections.push({
        title: config.label,
        text,
      });
    }
  }

  if (summarySections.length === 0) {
    const leadPayload = await fetchWikiSectionHtml(wikiApiUrl, pageTitle, '0', signal, fetchJson);
    if (leadPayload?.parse) {
      const summary = extractSummaryFromHtml(leadPayload.parse?.text?.['*'] || '', 2);
      if (summary) {
        summarySections.push({
          title: 'Resumo',
          text: summary,
        });
      }
    }
  }

  return {
    title: pageTitle,
    sections: summarySections,
    url: buildWikiUrl(wikiBaseUrl, pageTitle),
  };
};

const fetchWikiPageBySearch = async (options) => {
  const { term, signal, wikiApiUrl, fetchJson } = options;

  const params = new URLSearchParams({
    action: 'query',
    format: 'json',
    origin: '*',
    list: 'search',
    srlimit: '1',
    srsearch: term,
  });

  const payload = await fetchJson(`${wikiApiUrl}?${params.toString()}`, signal);
  const result = payload.query?.search?.[0];
  if (!result?.title) {
    return null;
  }

  return fetchWikiPageByTitle({
    ...options,
    title: result.title,
  });
};

const translateWikiSections = async (sections, translationOptions) => {
  let translatedAny = false;
  let translatedAll = true;
  const translatedSections = [];

  for (const section of sections || []) {
    if (!section?.text) {
      translatedSections.push(section);
      continue;
    }
    try {
      const translatedText = await translateTextToPortuguese(section.text, translationOptions);
      if (translatedText && translatedText.trim()) {
        translatedAny = true;
        translatedSections.push({
          ...section,
          text: translatedText,
        });
      } else {
        translatedAll = false;
        translatedSections.push(section);
      }
    } catch {
      translatedAll = false;
      translatedSections.push(section);
    }
  }

  let translationStatus = 'original';
  if (translatedAny) {
    translationStatus = translatedAll ? 'translated' : 'partial';
  } else if ((sections || []).some((section) => section?.text)) {
    translationStatus = 'failed';
  }

  return {
    sections: translatedSections,
    translationStatus,
  };
};

export const getWikiTranslationStatusMessage = (translationStatus) => {
  if (translationStatus === 'translated') {
    return 'Resumo traduzido automaticamente para portugues.';
  }
  if (translationStatus === 'partial') {
    return 'Parte do resumo foi traduzida automaticamente.';
  }
  if (translationStatus === 'failed') {
    return 'Nao foi possivel traduzir agora. Exibindo o idioma original.';
  }
  return 'Resumo exibido no idioma original.';
};

export const createWikiSummaryService = (options) => {
  const {
    wikiBaseUrl,
    sectionConfig,
    fetchJson = defaultFetchJson,
    maxTranslateLength = DEFAULT_MAX_TRANSLATE_LENGTH,
    myMemoryMaxChunk = DEFAULT_MYMEMORY_MAX_CHUNK,
  } = options;

  const normalizedBaseUrl = (wikiBaseUrl || '').replace(/\/$/, '');
  const wikiApiUrl = `${normalizedBaseUrl}/api.php`;
  const translationOptions = {
    maxTranslateLength,
    myMemoryMaxChunk,
    myMemoryUrl: DEFAULT_MYMEMORY_TRANSLATE_URL,
    translateUrl: process.env.REACT_APP_TRANSLATE_URL || DEFAULT_TRANSLATE_URL,
    translateApiKey: process.env.REACT_APP_TRANSLATE_API_KEY,
  };

  const loadWikiSummary = async (term, signal) => {
    let result = await fetchWikiPageByTitle({
      title: term,
      signal,
      wikiApiUrl,
      wikiBaseUrl: normalizedBaseUrl,
      sectionConfig,
      fetchJson,
    });

    if (!result) {
      result = await fetchWikiPageBySearch({
        term,
        signal,
        wikiApiUrl,
        wikiBaseUrl: normalizedBaseUrl,
        sectionConfig,
        fetchJson,
      });
    }

    if (!result) {
      return null;
    }

    const translated = await translateWikiSections(result.sections || [], {
      ...translationOptions,
      signal,
    });

    return {
      ...result,
      ...translated,
    };
  };

  const resolveWikiPageUrl = (wikiData, fallbackTitle) => {
    if (wikiData?.url) {
      return wikiData.url;
    }
    const title = wikiData?.title || fallbackTitle;
    return buildWikiUrl(normalizedBaseUrl, title);
  };

  return {
    buildWikiUrl: (title) => buildWikiUrl(normalizedBaseUrl, title),
    resolveWikiPageUrl,
    loadWikiSummary,
  };
};
